import { ProductCarouselProductStyles } from "@NA/Overrides/Components/ProductCarouselProduct";
import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import { getPageLinkByNodeId, useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import getColor from "@insite/mobius/utilities/getColor";
import React, { useEffect, useState } from "react";
import { ThemeProps, css, withTheme } from "styled-components";
import ADIColors from "@NA/Utils/ADIColors";
import { LinkPresentationProps } from "@insite/mobius/Link";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { StyledButton } from "@insite/mobius/Clickable";
import ADICategoryBrands from "@NA/Components/Brands/ADICategoryBrands";

const enum fields {
    title = "title",
    categoryName = "categoryName",
    brands = "brands",
}

interface NameArray {
    name: LinkFieldValue;
    brands: string[];
}

interface StructuredData {
    name: string;
    brands: string[];
}

interface TabModel {
    fields: NameArray;
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.categoryName]: TabModel[];
    };
}

export interface ADICategoryBrandsGalleryStyles {
    errorMessageText?: TypographyPresentationProps;
    titleText?: TypographyPresentationProps;
    urlText?: InjectableCss;
    skipCarouselButton?: SkipNavStyles;
    mainContainer?: GridContainerProps;
    prevArrowGridItem?: GridItemProps;
    prevArrowButton?: ButtonPresentationProps;
    nextArrowGridItem?: GridItemProps;
    nextArrowButton?: ButtonPresentationProps;
    carouselGridItem?: GridItemProps;
    carouselWrapper?: InjectableCss;
    carouselContainer?: InjectableCss;
    carouselSlidesContainer?: InjectableCss;
    carouselSlide?: InjectableCss;
    carouselSlideInner?: InjectableCss;
    carouselProductStyles?: ProductCarouselProductStyles;
    dotsContainerWrapper?: InjectableCss;
    dotButton?: ButtonPresentationProps;
    img?: InjectableCss;
    bigImg?: InjectableCss;
    buttonLink?: LinkPresentationProps;
    smallImgContainer?: InjectableCss;
    anchorStyles?: LinkPresentationProps;
}

export const adiCategoryBrandsGalleryStyles: ADICategoryBrandsGalleryStyles = {
    anchorStyles: {
        css: css`
            display: inline-block;
            height: 610px;
            width: 100%;
            // @media screen and (min-width: 768px) and (max-width: 1080px){
            @media only screen and (max-width: 1024px) {
                height: 312px !important;
            }
        `,
    },
    smallImgContainer: {
        css: css`
            position: absolute;
            bottom: 0;
            background: ${getColor("text.disabled")};
            opacity: 1;
            width: 100%;
            display: flex;
            align-items: center;
            height: 120px;
            // @media screen and (min-width: 768px) and (max-width: 1080px){
            @media only screen and (max-width: 1024px) {
                opacity: 0.7;
                height: 88px;
                background: ${getColor("common.background")};
            }
        `,
    },
    errorMessageText: {
        color: "danger",
        css: css`
            margin-top: 20px;
        `,
    },
    titleText: {
        variant: "h2",
        css: css`
            text-align: start;
            margin-bottom: 0px;
            font-size: 36px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: 26px;
                    `,
                    css`
                        font-size: 26px;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    urlText: {
        css: css`
            margin-bottom: 12px;
            > span {
                font-size: 14px;
                font-weight: bold;
                font-style: normal;
                line-height: normal;
                letter-spacing: normal;
                color: ${getColor("text.main")} !important;
                text-transform: uppercase;
                &:hover {
                    text-decoration: underline;
                }
            }
        `,
    },
    mainContainer: {
        gap: 0,
        css: css`
            position: relative;
        `,
    },
    prevArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            justify-content: flex-start;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                        position: absolute;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                ])}
        `,
    },
    prevArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            border: 0;
            padding: 0;
            position: absolute;
            top: 8%;
            right: 5%;
            transform: rotate(180deg);
            background: url(https://cdn.adiglobaldistribution.us/userfile-na/US/userfiles/Images/Themes/newicons/arrow-carousel-next-rd.svg)
                no-repeat;
            border: 0;
            width: 28px !important;
            height: 28px !important;
            opacity: 1;
            margin-bottom: 0;
            background-size: 28px 28px !important;
            z-index: 9;
            &:disabled {
                background: url(https://cdn.adiglobaldistribution.us/userfile-na/US/userfiles/Images/ArrowCarouselright-2.svg)
                    no-repeat;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        right: 0%;
                        top: 31%;
                        display: none;
                    `,
                    css`
                        top: 9%;
                        right: 10%;
                    `,
                    css`
                        top: -12%;
                        right: 12%;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    nextArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            justify-content: flex-end;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                        position: absolute;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                ])}
        `,
    },
    nextArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            border: 0;
            padding: 0;
            position: absolute;
            top: 29%;
            right: 2%;
            background: url(https://cdn.adiglobaldistribution.us/userfile-na/US/userfiles/Images/Themes/newicons/arrow-carousel-next-rd.svg)
                no-repeat;
            border: 0;
            width: 28px !important;
            height: 28px !important;
            opacity: 1;
            margin-bottom: 0;
            background-size: 28px 28px !important;
            z-index: 9;
            &:disabled {
                background: url(https://cdn.adiglobaldistribution.us/userfile-na/US/userfiles/Images/ArrowCarouselright-2.svg)
                    no-repeat;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        right: 10%;
                        top: 15%;
                        display: none;
                    `,
                    css`
                        top: 9%;
                        right: 7%;
                    `,
                    css`
                        top: 9%;
                        right: 7%;
                    `,

                    null,
                    null,
                ])}
        `,
    },
    carouselGridItem: {
        width: 10,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                ])}
        `,
    },
    carouselWrapper: {
        css: css`
            width: 100%;
            position: relative;
        `,
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
        `,
    },
    carouselSlidesContainer: {
        css: css`
            display: flex;
        `,
    },
    carouselSlide: {
        css: css`
            padding: 0;
            position: relative;
            flex: 0 0 auto;
            flex-basis: 20%;
            max-width: 20%;
            padding: 0 10px;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                        width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                        width: 100%;
                    `,
                    css`
                        flex-basis: 33.333%;
                        max-width: 33.333%;
                        width: 33.333%;
                    `,
                    css`
                        flex-basis: 33.333%;
                        max-width: 33.333%;
                        width: 33.333%;
                    `,
                    css`
                        flex-basis: 33.333%;
                        max-width: 33.333%;
                        width: 33.333%;
                    `,
                ])}
        `,
    },
    carouselSlideInner: {
        css: css`
            width: 100%;
            height: 610px;
            position: relative;
            padding: 0px;
            display: flex;
            flex-direction: column;
            // @media screen and (min-width: 768px) and (max-width: 1080px){
            @media only screen and (max-width: 1024px) {
                height: 449px !important;
            }
            > ${StyledButton} {
                > ${TypographyStyle} {
                    display: inline-block;
                    height: 610px;
                    width: 100%;
                    // @media screen and (min-width: 768px) and (max-width: 1080px){
                    @media only screen and (max-width: 1024px) {
                        height: 449px !important;
                    }
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: flex;
                        align-items: center;
                    `,
                    css`
                        display: flex;
                        align-items: center;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    dotsContainerWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            margin-top: 24px;
            position: absolute;
            width: 100%;
            max-width: 1420px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 93%;
                    `,
                    null,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    dotButton: {
        css: css`
            min-height: 7px;
            max-width: 55px;
            height: 3px;
            width: 10%;
            padding: 0;
            margin: 0 4px;
            border: 0px;
            border-radius: 2px;
            background: ${getColor("common.border")};
            background-color: ${ADIColors.gray16};
            border: 1px solid ${ADIColors.gray16};
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin: 0 0;
                    `,
                    null,
                    null,
                    null,
                    null,
                ])}
            &:hover {
                background: ${ADIColors.white};
            }
            &:active {
                background-color: ${ADIColors.darkBlue1};
                border: 1px solid ${ADIColors.darkBlue1};
            }
            &:focus {
                outline: none;
            }
        `,
    },
    img: {
        css: css`
            margin: 0 auto;
            height: 100%;
            padding: 20px 0;
            width: 53.5%;
            // @media screen and (min-width: 768px) and (max-width: 1080px){
            @media only screen and (max-width: 1024px) {
                width: 50%;
                padding: 10px 0;
            }
        `,
    },
    bigImg: {
        css: css`
            width: 100%;
            height: 100%;
        `,
    },
    buttonLink: {
        css: css`
            font-size: 16px;
            font-weight: bold;
            color: ${getColor("common.background")};
            > ${TypographyStyle} {
                color: ${ADIColors.white};
                text-decoration: none;
            }
            ${TypographyStyle} :hover {
                color: ${ADIColors.white};
                text-decoration: none;
            }
        `,
    },
};

type Props = OwnProps & HasShellContext & HasCategoryContext & ThemeProps<BaseTheme>;

const ADICategoryBrandsGallery: React.FC<Props> = ({ fields, category }) => {
    const [filteredFields, setFilteredFields] = useState<StructuredData | undefined>(undefined);
    const [currentCategoryFields, setCurrentCategoryFields] = React.useState<TabModel>();
    const links = useGetLinks(fields.categoryName, o => o.fields.name);

    useEffect(() => {
        var categoryPath = "";
        if (category?.path && category?.path.split("/").length > 4) {
            const pathName = category?.path.split("/");
            pathName.pop();
            categoryPath = pathName.join("/");
        }

        const FilteredData: StructuredData[] = fields.categoryName
            .map((category, index) => {
                if (category.fields.name.type === "Category") {
                    return {
                        name: links[index].url || "",
                        brands: category.fields.brands,
                    };
                }
                return undefined;
            })
            .filter(data => {
                const catPath = categoryPath ? categoryPath : category?.path;
                if (data !== undefined && data.name === catPath) {
                    return data;
                }
            }) as StructuredData[];

        if (FilteredData.length) {
            setFilteredFields(FilteredData[0]);
            setCurrentCategoryFields({ fields: FilteredData[0] } as unknown as TabModel);
        } else {
            setFilteredFields(undefined);
        }
    }, [category, fields]);

    if (!currentCategoryFields?.fields.brands) {
        return null;
    }

    return <ADICategoryBrands brandIds={currentCategoryFields?.fields.brands} title={fields.title} />;
};

const widgetModule: WidgetModule = {
    component: withIsInShell(withCategory(withTheme(ADICategoryBrandsGallery))),
    definition: {
        group: "Basic",
        icon: "Image",
        displayName: "ADI Category Brands",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                sortOrder: 0,
            },
            {
                name: fields.categoryName,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    const {
                        name: { type, value },
                    } = item.fields;

                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return link?.title;
                    }
                    if (type === "Category") {
                        let displayValue = "";
                        const categories = (state as any).pageEditor.categories;
                        if (!categories) {
                            displayValue = "Loading...";
                        } else {
                            const matchingCategories = categories.filter((o: { id: string }) => o.id === value);
                            if (matchingCategories.length === 0) {
                                displayValue = "Unknown";
                            } else {
                                displayValue = matchingCategories[0].shortDescription;
                            }
                        }

                        return displayValue;
                    }
                    if (type === "Url") {
                        return value;
                    }
                    return value;
                },
                defaultValue: [],
                fieldType: "General",
                sortOrder: 1,
                validate: value => {
                    const allCatIds = value.map(item => item.fields.name.value);

                    if (new Set(allCatIds).size !== allCatIds.length) {
                        return "Category Already Exists";
                    } else {
                        return "";
                    }
                },
                fieldDefinitions: [
                    {
                        name: "name",
                        displayName: "Category Name",
                        sortOrder: 0,
                        editorTemplate: "LinkField",
                        isRequired: true,
                        defaultValue: {
                            value: "",
                            type: "Category",
                        },
                        allowUrls: () => {
                            return false;
                        },
                    },
                    {
                        name: fields.brands,
                        displayName: "Select Brands",
                        editorTemplate: "SelectBrandsField",
                        defaultValue: [],
                        sortOrder: 1,
                    },
                ],
            },
        ],
    },
};
export default widgetModule;
